<template lang="pug">
div
  .d-flex.justify-content-between.align-items-center.mb-3
    h4.text-lg.text-bold.mb-0 結果シナリオ一覧
    span.custom-tooltip(:data-tooltip="simulatorTooltip")
      button.btn.btn-primary.btn-sm(
        @click="toggleSimulator",
        :disabled="patterns.length === 0"
      )
        i.fas.mr-1(:class="showSimulator ? 'fa-chevron-up' : 'fa-eye'")
        | {{ showSimulator ? '閉じる' : '回答結果プレビュー' }}

  .card.mb-4(v-if="showSimulator")
    .card-header.bg-primary.text-white
      h5.card-title.mb-0
        i.fas.fa-eye.mr-2
        | 回答結果プレビュー
    .card-body
      .row
        .col-md-6
          h6.mb-3 設問の回答を選択してください
          .form-group.mb-2(
            v-for="(collection, idx) in questionSlots",
            :key="`expand-sim-${idx}`",
            :class="{ 'question-slot-empty': !collection }"
          )
            label.mb-1 設問{{ idx + 1 }}
            select.form-control(
              v-if="collection",
              v-model="simulatorChoices[idx]",
              @change="updateSimulation",
              :disabled="simulatorDisabledQuestions[idx]"
            )
              option(value="", disabled, selected) {{ simulatorDisabledQuestions[idx] ? 'なし（スキップ）' : '選択してください' }}
              option(
                v-for="(choice, cIdx) in collection.choices.filter(c => !c._destroy)",
                :key="choice.id || choice.temp_id",
                :value="choice.id || choice.temp_id"
              ) {{ choice.buttonName }}
            select.form-control(v-else, disabled)
              option(value="") -
        .col-md-6
          h6.mb-3 結果
          .alert.alert-light.border
            div(v-if="simulatedPattern")
              p.mb-2
                strong {{ getPatternLabel(simulatedPattern.index) }}
              .rounded.p-2.bg-white.text-center.result-box(v-if="simulatedPattern.image.imageData")
                img.img-fluid(:src="simulatedPattern.image.imageData", style="max-height: 120px; object-fit: contain;")
              .rounded.p-2.bg-white.text-center.result-box(v-else-if="simulatedPatternPopupInfo")
                img.img-fluid.mb-1(v-if="simulatedPatternPopupInfo.thumbnail_url", :src="simulatedPatternPopupInfo.thumbnail_url", style="max-height: 120px; object-fit: contain;")
                .small.text-truncate(style="max-width: 150px;") 接続先：{{ simulatedPatternPopupInfo.name || '(未設定)' }}
              .rounded.p-2.bg-white.text-center.result-box(v-else-if="simulatedPattern.image.iframeEmbedUrl")
                .small.text-muted iframe
                .small.text-truncate.text-muted(style="max-width: 150px;", :title="simulatedPattern.image.iframeEmbedUrl")
                  | {{ simulatedPattern.image.iframeEmbedUrl }}
              .rounded.p-2.bg-white.text-center.result-box(v-else)
                .small.text-muted 結果画像無し
                template(v-if="simulatedPattern.image.noLink")
                  .small.text-muted リンクなし
                template(v-else-if="simulatedPattern.image.link")
                  .small リンクあり{{ linkTabLabel(simulatedPattern.image) }}
                  .small.text-truncate.text-muted(style="max-width: 150px;", :title="simulatedPattern.image.link")
                    | {{ simulatedPattern.image.link }}
            div(v-else-if="allChoicesSelected")
              p.mb-2
                strong デフォルト
              template(v-if="defaultPatternObject")
                .rounded.p-2.bg-white.text-center.result-box(v-if="defaultPatternObject.imageData")
                  img.img-fluid(:src="defaultPatternObject.imageData", style="max-height: 120px; object-fit: contain;")
                .rounded.p-2.bg-white.text-center.result-box(v-else-if="defaultPatternPopupInfo")
                  img.img-fluid.mb-1(v-if="defaultPatternPopupInfo.thumbnail_url", :src="defaultPatternPopupInfo.thumbnail_url", style="max-height: 120px; object-fit: contain;")
                  .small.text-truncate(style="max-width: 150px;") 接続先：{{ defaultPatternPopupInfo.name || '(未設定)' }}
                .rounded.p-2.bg-white.text-center.result-box(v-else-if="defaultPatternObject.iframeEmbedUrl")
                  .small.text-muted iframe
                  .small.text-truncate.text-muted(style="max-width: 150px;", :title="defaultPatternObject.iframeEmbedUrl")
                    | {{ defaultPatternObject.iframeEmbedUrl }}
                .rounded.p-2.bg-white.text-center.result-box(v-else)
                  .small.text-muted 結果画像無し
                  template(v-if="defaultPatternObject.noLink")
                    .small.text-muted リンクなし
                  template(v-else-if="defaultPatternObject.link")
                    .small リンクあり{{ linkTabLabel(defaultPatternObject) }}
                    .small.text-truncate.text-muted(style="max-width: 150px;", :title="defaultPatternObject.link")
                      | {{ defaultPatternObject.link }}
            div.result-placeholder(v-else)
              p.text-muted.text-center.mb-0 すべての設問でボタンを選択してください
          .mt-3
            button.btn.btn-secondary.btn-sm(@click="resetSimulator")
              i.fas.fa-redo.mr-1
              | リセット
</template>

<script>
import { linkTabLabel } from '../utils/linkTabLabel';

const MAX_SCENARIO_IMAGES = 5;

export default {
  name: 'ScenarioPopupSimulator',

  props: {
    activeCollections: {
      type: Array,
      default: () => []
    },
    patterns: {
      type: Array,
      default: () => []
    },
    defaultPatternObject: {
      type: Object,
      default: null
    },
    chainedPopupOptions: {
      type: Array,
      default: () => []
    },
  },

  data() {
    return {
      showSimulator: false,
      simulatorChoices: {},
    };
  },

  computed: {
    questionSlots() {
      const slots = [];
      for (let i = 0; i < MAX_SCENARIO_IMAGES; i++) {
        slots.push(this.activeCollections[i] || null);
      }
      return slots;
    },

    simulatorTooltip() {
      return this.patterns.length === 0 ? 'シナリオを1つ以上登録してください' : '';
    },

    hasAnySelection() {
      return this.activeCollections.some((_, idx) => this.isQuestionAnswered(idx));
    },

    simulatorDisabledQuestions() {
      if (!this.hasAnySelection) return {};

      const matchingPattern = this.patterns.find(pattern =>
        this.patternMatchesSelectedChoices(pattern)
      );
      if (!matchingPattern) return {};

      return this.activeCollections.reduce((disabled, _, idx) => {
        if (matchingPattern[`q${idx + 1}ChoiceId`] === null) disabled[idx] = true;
        return disabled;
      }, {});
    },

    allChoicesSelected() {
      return this.activeCollections.every((_, idx) =>
        this.simulatorDisabledQuestions[idx] || this.isQuestionAnswered(idx)
      );
    },

    simulatedPattern() {
      if (!this.allChoicesSelected) return null;

      const index = this.patterns.findIndex(pattern =>
        this.patternMatchesAllChoices(pattern)
      );
      if (index === -1) return null;

      return { image: this.patterns[index], index };
    },

    simulatedPatternPopupInfo() {
      if (!this.simulatedPattern) return null;
      if (!this.simulatedPattern.image.nextScenarioPopupId) return null;
      return this.getNextPopupInfo(this.simulatedPattern.image);
    },

    defaultPatternPopupInfo() {
      if (!this.defaultPatternObject) return null;
      if (!this.defaultPatternObject.nextScenarioPopupId) return null;
      return this.getNextPopupInfo(this.defaultPatternObject);
    },
  },

  watch: {
    patterns(newVal) {
      if (newVal.length === 0) this.showSimulator = false;
    },
  },

  methods: {
    linkTabLabel,
    toggleSimulator() {
      this.showSimulator = !this.showSimulator;
      if (this.showSimulator) {
        this.resetSimulator();
        this.$emit('load-popup-options');
      }
    },

    updateSimulation() {
      this.simulatorChoices = { ...this.simulatorChoices };

      this.$nextTick(() => {
        const choices = { ...this.simulatorChoices };
        let changed = false;

        this.activeCollections.forEach((_, idx) => {
          if (this.simulatorDisabledQuestions[idx] && choices[idx] !== '') {
            choices[idx] = '';
            changed = true;
          }
        });

        if (changed) this.simulatorChoices = choices;
      });
    },

    resetSimulator() {
      const choices = {};
      this.activeCollections.forEach((_, idx) => { choices[idx] = ''; });
      this.simulatorChoices = choices;
    },

    isQuestionAnswered(idx) {
      const choice = this.simulatorChoices[idx];
      return choice !== undefined && choice !== '';
    },

    patternMatchesSelectedChoices(pattern) {
      return this.activeCollections.every((_, idx) => {
        if (!this.isQuestionAnswered(idx)) return true;

        const patternChoiceId = pattern[`q${idx + 1}ChoiceId`];
        const isWildcard = patternChoiceId === 0 || patternChoiceId === null;
        if (isWildcard) return true;

        return patternChoiceId === parseInt(this.simulatorChoices[idx]);
      });
    },

    patternMatchesAllChoices(pattern) {
      return this.activeCollections.every((_, idx) => {
        const patternChoiceId = pattern[`q${idx + 1}ChoiceId`];
        const isWildcard = patternChoiceId === 0 || patternChoiceId === null;
        if (isWildcard) return true;

        return patternChoiceId === parseInt(this.simulatorChoices[idx]);
      });
    },

    getNextPopupInfo(pattern) {
      return this.chainedPopupOptions.find(p => p.id === pattern.nextScenarioPopupId) || {
        name: pattern.nextScenarioPopupName,
        thumbnail_url: pattern.nextScenarioPopupThumbnailUrl
      };
    },

    getPatternLabel(index) {
      return `#${index + 1}`;
    },
  },
};
</script>
<style scoped>
  .custom-tooltip {
    position: relative;
    display: inline-block;
  }

  .custom-tooltip[data-tooltip]::before,
  .custom-tooltip[data-tooltip]::after {
    visibility: hidden;
    opacity: 0;
    pointer-events: none;
    transition: all 0.2s ease-in-out;
    z-index: 1050;
  }

  .custom-tooltip[data-tooltip]::before {
    content: attr(data-tooltip);
    position: absolute;
    bottom: calc(100% + 8px);
    left: 50%;
    transform: translateX(-50%);
    padding: 8px 12px;
    background: linear-gradient(135deg, #343a40 0%, #495057 100%);
    color: #fff;
    font-size: 12px;
    font-weight: 500;
    line-height: 1.4;
    white-space: nowrap;
    border-radius: 6px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  .custom-tooltip[data-tooltip]::after {
    content: '';
    position: absolute;
    bottom: calc(100% + 2px);
    left: 50%;
    transform: translateX(-50%);
    border: 6px solid transparent;
    border-top-color: #495057;
  }

  .custom-tooltip[data-tooltip]:hover::before,
  .custom-tooltip[data-tooltip]:hover::after {
    visibility: visible;
    opacity: 1;
  }

  .custom-tooltip[data-tooltip=""]::before,
  .custom-tooltip[data-tooltip=""]::after {
    display: none;
  }

  .result-box {
    height: 140px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    overflow: hidden;
  }

  .result-placeholder {
    height: 164px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .form-group.question-slot-empty {
    opacity: 0.55;
  }

  .form-group.question-slot-empty label {
    color: #adb5bd;
  }

  .form-group.question-slot-empty select:disabled {
    background-color: #e9ecef;
    color: #adb5bd;
  }

  .card-body button:disabled {
    background-color: #cccccc !important;
    border-color: #cccccc !important;
    color: #666666 !important;
    opacity: 1 !important;
  }

</style>
